import * as React from 'react';
import { Dropdown as BoostrapDropdown } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { useLoadableState } from 'af-root/hooks/useLoadable';

import type { W_MaterialCost_FindForWorkSummary_VM_Single } from 'ab-api/web/materialCost/findForWorkSummary';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { useToggle } from 'af-utils/react.util';

import type MaterialCostSource from 'ab-enums/materialCostSource.enum';

import MaterialRateSource from 'af-components/MaterialRateSource';

import * as MaterialCostActions from 'af-actions/materialCost';
import * as WorkSummaryDetailsActions from 'af-actions/workSummaryDetails';

import { MaterialCosts } from './MaterialCosts';
import styles from './styles.module.scss';

const MAX_DROPDOWN_MENU_HEIGHT = 400 * 0.9;

interface OwnProps {
	billableWorkId: number;
	fieldReportId: number;
	subJobId: number;
	workSummaryDetailIds: number[];
	selectedMaterialName: Nullable<string>;
	selectedMaterialCostSource: Nullable<MaterialCostSource>;
	readonly: boolean;
	unit: Nullable<QuantityUnitType>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const MaterialCostDropdown: React.FC<Props> = (props) => {
	const {
		billableWorkId,
		fieldReportId,
		workSummaryDetailIds,
		selectedMaterialCostSource,
		selectedMaterialName,
		readonly,
		subJobId,
		unit,
		findMaterialCosts,
		assignMaterialCost,
	} = props;

	const dropdownToggleRef = React.useRef<HTMLDivElement>(null);

	const { value: isOpen, setToFalse: close, setToTrue: open } = useToggle();
	const [dropup, setDropup] = React.useState(false);

	const fetchMaterialCosts = React.useCallback(async () => {
		return await findMaterialCosts(fieldReportId, subJobId, billableWorkId);
	}, [billableWorkId, fieldReportId, findMaterialCosts, subJobId]);

	const { load: loadMaterialCosts, data } = useLoadableState(fetchMaterialCosts);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}
		loadMaterialCosts();
	}, [isOpen, loadMaterialCosts]);

	const renderSelected = React.useCallback(() => {
		if (!selectedMaterialCostSource || !selectedMaterialName) {
			return <span className="dropdown-toggle__placeholder">Add Material Cost</span>;
		}
		return (
			<div className={styles.selectedItem}>
				<MaterialRateSource rateSource={selectedMaterialCostSource} />
				<div className={styles.name}>{selectedMaterialName}</div>
			</div>
		);
	}, [selectedMaterialCostSource, selectedMaterialName]);

	const handleToggle = React.useCallback((newIsOpen: boolean) => {
		if (newIsOpen && dropdownToggleRef.current) {
			const bodyRect = document.body.getClientRects()[0];
			const dropdownRect = dropdownToggleRef.current.getClientRects()[0] || {};
			const dropdownTogglePositionY = dropdownRect.y || 0;
			const isDropup = dropdownTogglePositionY > (bodyRect.height - MAX_DROPDOWN_MENU_HEIGHT);
			setDropup(isDropup);
		}
		if (newIsOpen) {
			open();
		} else {
			close();
		}
	}, [close, open]);

	const handleAssignCost = React.useCallback(async (materialCost: W_MaterialCost_FindForWorkSummary_VM_Single) => {
		const { materialId, source } = materialCost;

		close();

		await assignMaterialCost({ fieldReportId, workSummaryDetailIds, materialCostSource: source, materialId });
	}, [assignMaterialCost, close, fieldReportId, workSummaryDetailIds]);

	const dropdownToggleClassName = React.useMemo(() => {
		return bemBlock('dropdown-toggle', {
			'with-caret': true,
		});
	}, []);

	const dropdownToggleContainerClassName = React.useMemo(() => {
		return bemBlock('dropdown-toggle', {
			bordered: true,
			white: true,
		});
	}, []);

	const className = React.useMemo(() => {
		return bemElement('field-report__work-summary__table__row__data__cell', 'dropdown');
	}, []);

	const dropdownMenuClassName = React.useMemo(() => {
		const base = bemBlock('dropdown-menu', {
			'scroll-container': true,
		});

		return `${base} ${styles.menu}`;
	}, []);

	const handleClear = React.useCallback(async () => {
		await assignMaterialCost({ fieldReportId, workSummaryDetailIds, materialCostSource: null, materialId: null });
	}, [assignMaterialCost, fieldReportId, workSummaryDetailIds]);

	const showClearButton = !!selectedMaterialCostSource;

	return (
		<BoostrapDropdown className={className} drop={dropup ? 'up' : undefined} onToggle={handleToggle} show={isOpen}>
			<BoostrapDropdown.Toggle className={dropdownToggleContainerClassName} disabled={readonly}>
				<div className={dropdownToggleClassName}>
					<div className="dropdown-menu__selected">
						<span className="dropdown-menu__selected-text">{renderSelected()}</span>
						<span className="icon-down dropdown-menu__with-caret"></span>
					</div>
					{showClearButton && (
						<span className={`icon-close dropdown-toggle__clear ${readonly ? 'disabled' : ''}`} onClick={handleClear} />
					)}
				</div>
			</BoostrapDropdown.Toggle>
			<BoostrapDropdown.Menu className={dropdownMenuClassName}>
				{!!data?.materialCostsBySource && (
					<MaterialCosts materialCostsBySource={data.materialCostsBySource} onDate={data.onDate} onSelect={handleAssignCost} targetUnit={unit} />
				)}
			</BoostrapDropdown.Menu>
		</BoostrapDropdown>
	);
};

const mapDispatchToProps = {
	findMaterialCosts: MaterialCostActions.findForWorkSummary,
	assignMaterialCost: WorkSummaryDetailsActions.assignMaterialCost,
};

const connector = connect(null, mapDispatchToProps);

export default connector(MaterialCostDropdown);
